import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  writeBatch
} from 'firebase/firestore';
import { db } from '../lib/firebase';

// Content Management Types
export interface ContentCategory {
  id: string;
  name: string;
  description: string;
  parentId?: string;
  subcategories: string[];
  contentCount: number;
  isActive: boolean;
  createdAt: any;
  updatedAt: any;
}

export interface ContentTag {
  id: string;
  name: string;
  description?: string;
  color: string;
  usageCount: number;
  isTrending: boolean;
  createdAt: any;
  updatedAt: any;
}

export interface ContentCollection {
  id: string;
  name: string;
  description: string;
  creatorId: string;
  creatorName: string;
  isPublic: boolean;
  isCurated: boolean;
  contentIds: string[];
  contentCount: number;
  followers: number;
  tags: string[];
  createdAt: any;
  updatedAt: any;
}

export interface ContentVersion {
  id: string;
  contentId: string;
  version: number;
  title: string;
  description: string;
  changes: string[];
  createdBy: string;
  createdAt: any;
  isActive: boolean;
}

export interface ScheduledContent {
  id: string;
  contentId: string;
  title: string;
  scheduledDate: any;
  status: 'scheduled' | 'published' | 'cancelled';
  createdBy: string;
  createdAt: any;
}

export interface ContentArchive {
  id: string;
  contentId: string;
  archivedBy: string;
  reason: string;
  archivedAt: any;
  isPermanent: boolean;
}

// Content Management Service
export class ContentManagementService {
  // Category Management
  static async createCategory(
    name: string, 
    description: string, 
    parentId?: string
  ): Promise<string> {
    try {
      const categoryData: Omit<ContentCategory, 'id'> = {
        name,
        description,
        parentId,
        subcategories: [],
        contentCount: 0,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'categories'), categoryData);
      
      // Update parent category if exists
      if (parentId) {
        await updateDoc(doc(db, 'categories', parentId), {
          subcategories: arrayUnion(docRef.id),
          updatedAt: serverTimestamp()
        });
      }

      return docRef.id;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  static async getCategories(): Promise<ContentCategory[]> {
    try {
      const q = query(
        collection(db, 'categories'),
        where('isActive', '==', true),
        orderBy('name', 'asc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ContentCategory));
    } catch (error) {
      console.error('Error getting categories:', error);
      throw error;
    }
  }

  static async updateCategory(
    categoryId: string, 
    updates: Partial<ContentCategory>
  ): Promise<void> {
    try {
      await updateDoc(doc(db, 'categories', categoryId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  }

  static async deleteCategory(categoryId: string): Promise<void> {
    try {
      // Get category to check for subcategories
      const categoryDoc = await getDoc(doc(db, 'categories', categoryId));
      const category = categoryDoc.data() as ContentCategory;
      
      if (category.subcategories.length > 0) {
        throw new Error('Cannot delete category with subcategories');
      }

      // Soft delete
      await updateDoc(doc(db, 'categories', categoryId), {
        isActive: false,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }

  // Tag Management
  static async createTag(
    name: string, 
    description?: string, 
    color: string = '#6366f1'
  ): Promise<string> {
    try {
      const tagData: Omit<ContentTag, 'id'> = {
        name: name.toLowerCase(),
        description,
        color,
        usageCount: 0,
        isTrending: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'tags'), tagData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating tag:', error);
      throw error;
    }
  }

  static async getTags(): Promise<ContentTag[]> {
    try {
      const q = query(
        collection(db, 'tags'),
        orderBy('usageCount', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ContentTag));
    } catch (error) {
      console.error('Error getting tags:', error);
      throw error;
    }
  }

  static async getTrendingTags(): Promise<ContentTag[]> {
    try {
      const q = query(
        collection(db, 'tags'),
        where('isTrending', '==', true),
        orderBy('usageCount', 'desc'),
        limit(20)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ContentTag));
    } catch (error) {
      console.error('Error getting trending tags:', error);
      throw error;
    }
  }

  static async updateTagUsage(tagId: string, increment: boolean = true): Promise<void> {
    try {
      const tagRef = doc(db, 'tags', tagId);
      const incrementValue = increment ? 1 : -1;
      
      await updateDoc(tagRef, {
        usageCount: incrementValue,
        updatedAt: serverTimestamp()
      });

      // Check if tag should be trending (usage > 10)
      const tagDoc = await getDoc(tagRef);
      const tag = tagDoc.data() as ContentTag;
      
      if (tag.usageCount >= 10 && !tag.isTrending) {
        await updateDoc(tagRef, { isTrending: true });
      } else if (tag.usageCount < 10 && tag.isTrending) {
        await updateDoc(tagRef, { isTrending: false });
      }
    } catch (error) {
      console.error('Error updating tag usage:', error);
      throw error;
    }
  }

  // Collection Management
  static async createCollection(
    name: string,
    description: string,
    creatorId: string,
    creatorName: string,
    isPublic: boolean = true,
    tags: string[] = []
  ): Promise<string> {
    try {
      const collectionData: Omit<ContentCollection, 'id'> = {
        name,
        description,
        creatorId,
        creatorName,
        isPublic,
        isCurated: false,
        contentIds: [],
        contentCount: 0,
        followers: 0,
        tags,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'collections'), collectionData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating collection:', error);
      throw error;
    }
  }

  static async getCollections(userId?: string): Promise<ContentCollection[]> {
    try {
      let q;
      if (userId) {
        q = query(
          collection(db, 'collections'),
          where('creatorId', '==', userId),
          orderBy('createdAt', 'desc')
        );
      } else {
        q = query(
          collection(db, 'collections'),
          where('isPublic', '==', true),
          orderBy('followers', 'desc')
        );
      }
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ContentCollection));
    } catch (error) {
      console.error('Error getting collections:', error);
      throw error;
    }
  }

  static async addToCollection(
    collectionId: string, 
    contentId: string
  ): Promise<void> {
    try {
      const collectionRef = doc(db, 'collections', collectionId);
      
      await updateDoc(collectionRef, {
        contentIds: arrayUnion(contentId),
        contentCount: 1,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error adding to collection:', error);
      throw error;
    }
  }

  static async removeFromCollection(
    collectionId: string, 
    contentId: string
  ): Promise<void> {
    try {
      const collectionRef = doc(db, 'collections', collectionId);
      
      await updateDoc(collectionRef, {
        contentIds: arrayRemove(contentId),
        contentCount: -1,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error removing from collection:', error);
      throw error;
    }
  }

  static async followCollection(
    collectionId: string, 
    userId: string
  ): Promise<void> {
    try {
      const collectionRef = doc(db, 'collections', collectionId);
      
      await updateDoc(collectionRef, {
        followers: 1,
        updatedAt: serverTimestamp()
      });

      // Add to user's followed collections
      await addDoc(collection(db, 'userCollections'), {
        userId,
        collectionId,
        followedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error following collection:', error);
      throw error;
    }
  }

  // Content Versioning
  static async createContentVersion(
    contentId: string,
    title: string,
    description: string,
    changes: string[],
    createdBy: string
  ): Promise<string> {
    try {
      // Get current version number
      const versionsQuery = query(
        collection(db, 'contentVersions'),
        where('contentId', '==', contentId),
        orderBy('version', 'desc'),
        limit(1)
      );
      
      const snapshot = await getDocs(versionsQuery);
      const currentVersion = snapshot.empty ? 0 : snapshot.docs[0].data().version;

      const versionData: Omit<ContentVersion, 'id'> = {
        contentId,
        version: currentVersion + 1,
        title,
        description,
        changes,
        createdBy,
        createdAt: serverTimestamp(),
        isActive: true
      };

      const docRef = await addDoc(collection(db, 'contentVersions'), versionData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating content version:', error);
      throw error;
    }
  }

  static async getContentVersions(contentId: string): Promise<ContentVersion[]> {
    try {
      const q = query(
        collection(db, 'contentVersions'),
        where('contentId', '==', contentId),
        orderBy('version', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ContentVersion));
    } catch (error) {
      console.error('Error getting content versions:', error);
      throw error;
    }
  }

  static async rollbackToVersion(
    contentId: string, 
    versionId: string
  ): Promise<void> {
    try {
      const versionDoc = await getDoc(doc(db, 'contentVersions', versionId));
      const version = versionDoc.data() as ContentVersion;
      
      if (!version) {
        throw new Error('Version not found');
      }

      // Update content with version data
      await updateDoc(doc(db, 'content', contentId), {
        title: version.title,
        description: version.description,
        updatedAt: serverTimestamp()
      });

      // Mark all other versions as inactive
      const versionsQuery = query(
        collection(db, 'contentVersions'),
        where('contentId', '==', contentId)
      );
      
      const snapshot = await getDocs(versionsQuery);
      const batch = writeBatch(db);
      
      snapshot.docs.forEach(doc => {
        if (doc.id !== versionId) {
          batch.update(doc.ref, { isActive: false });
        }
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error rolling back to version:', error);
      throw error;
    }
  }

  // Content Scheduling
  static async scheduleContent(
    contentId: string,
    title: string,
    scheduledDate: Date,
    createdBy: string
  ): Promise<string> {
    try {
      const scheduleData: Omit<ScheduledContent, 'id'> = {
        contentId,
        title,
        scheduledDate,
        status: 'scheduled',
        createdBy,
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'scheduledContent'), scheduleData);
      return docRef.id;
    } catch (error) {
      console.error('Error scheduling content:', error);
      throw error;
    }
  }

  static async getScheduledContent(userId?: string): Promise<ScheduledContent[]> {
    try {
      let q;
      if (userId) {
        q = query(
          collection(db, 'scheduledContent'),
          where('createdBy', '==', userId),
          orderBy('scheduledDate', 'asc')
        );
      } else {
        q = query(
          collection(db, 'scheduledContent'),
          where('status', '==', 'scheduled'),
          orderBy('scheduledDate', 'asc')
        );
      }
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ScheduledContent));
    } catch (error) {
      console.error('Error getting scheduled content:', error);
      throw error;
    }
  }

  static async publishScheduledContent(scheduleId: string): Promise<void> {
    try {
      const scheduleRef = doc(db, 'scheduledContent', scheduleId);
      
      await updateDoc(scheduleRef, {
        status: 'published',
        updatedAt: serverTimestamp()
      });

      // Update content visibility
      const scheduleDoc = await getDoc(scheduleRef);
      const schedule = scheduleDoc.data() as ScheduledContent;
      
      await updateDoc(doc(db, 'content', schedule.contentId), {
        visibility: 'public',
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error publishing scheduled content:', error);
      throw error;
    }
  }

  // Content Archiving
  static async archiveContent(
    contentId: string,
    reason: string,
    archivedBy: string,
    isPermanent: boolean = false
  ): Promise<string> {
    try {
      const archiveData: Omit<ContentArchive, 'id'> = {
        contentId,
        archivedBy,
        reason,
        archivedAt: serverTimestamp(),
        isPermanent
      };

      const docRef = await addDoc(collection(db, 'contentArchives'), archiveData);
      
      // Update content status
      await updateDoc(doc(db, 'content', contentId), {
        isArchived: true,
        archivedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return docRef.id;
    } catch (error) {
      console.error('Error archiving content:', error);
      throw error;
    }
  }

  static async unarchiveContent(contentId: string): Promise<void> {
    try {
      // Update content status
      await updateDoc(doc(db, 'content', contentId), {
        isArchived: false,
        archivedAt: null,
        updatedAt: serverTimestamp()
      });

      // Mark archive as inactive
      const archiveQuery = query(
        collection(db, 'contentArchives'),
        where('contentId', '==', contentId),
        where('isPermanent', '==', false)
      );
      
      const snapshot = await getDocs(archiveQuery);
      if (!snapshot.empty) {
        await updateDoc(snapshot.docs[0].ref, {
          isPermanent: true,
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error unarchiving content:', error);
      throw error;
    }
  }

  static async getArchivedContent(userId?: string): Promise<any[]> {
    try {
      let q;
      if (userId) {
        q = query(
          collection(db, 'content'),
          where('uploaderId', '==', userId),
          where('isArchived', '==', true),
          orderBy('archivedAt', 'desc')
        );
      } else {
        q = query(
          collection(db, 'content'),
          where('isArchived', '==', true),
          orderBy('archivedAt', 'desc')
        );
      }
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting archived content:', error);
      throw error;
    }
  }

  // Bulk Operations
  static async bulkArchiveContent(
    contentIds: string[],
    reason: string,
    archivedBy: string
  ): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      for (const contentId of contentIds) {
        // Create archive record
        const archiveRef = doc(collection(db, 'contentArchives'));
        batch.set(archiveRef, {
          contentId,
          archivedBy,
          reason,
          archivedAt: serverTimestamp(),
          isPermanent: false
        });

        // Update content status
        const contentRef = doc(db, 'content', contentId);
        batch.update(contentRef, {
          isArchived: true,
          archivedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      await batch.commit();
    } catch (error) {
      console.error('Error bulk archiving content:', error);
      throw error;
    }
  }

  static async bulkUpdateTags(
    contentIds: string[],
    tags: string[]
  ): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      for (const contentId of contentIds) {
        const contentRef = doc(db, 'content', contentId);
        batch.update(contentRef, {
          tags,
          updatedAt: serverTimestamp()
        });
      }
      
      await batch.commit();
    } catch (error) {
      console.error('Error bulk updating tags:', error);
      throw error;
    }
  }
}